/**
 * Trading-card-game **content**: the versioned Core Set v1 card pool, with monster
 * definitions derived from the shared {@link monsters bestiary} and then frozen.
 *
 * The card game (`cardGame.ts`) is a pure, clock-free, command-driven battle
 * engine in the duel / dice-wager / Rat-Swarm mould; this module owns the other
 * half of Slice 1 - **where cards come from**. Two rules keep the pool honest:
 *
 *  - **Core Set v1 is an explicit immutable list.** A card's mana cost
 *    is derived from the creature's power tier (the arena's weak->strong wave and
 *    elite tables, {@link ../sim/arena.ARENA_STAGES} / {@link
 *    ../sim/arena.ARENA_ELITE_SEQUENCE}, are the canonical grouping already in the
 *    codebase), with a small {@link CARD_TWEAKS hand-tweak table} for the handful
 *    of outliers the arena tiers do not place (the critter slimes, the named
 *    bosses) and keyword flavour. New bestiary entries do not silently enter a
 *    released set; a future set must add them deliberately.
 *  - **Every card name has four rarity definitions** (common / uncommon / rare /
 *    legendary). All four printings of a card name have identical mana, stats,
 *    abilities, and action effects, while pack availability may reserve selected
 *    chase identities for rare and legendary pulls.
 *
 * **Balance rescaling for 100-HP heroes (captain's instruction).** The PRD's
 * battle ruleset was written for 30-HP heroes; the captain raised hero HP to 100
 * (~3.33x). To keep match *length and turn pacing* matching the 30-HP design, the
 * monster stat formula's attack/health outputs are scaled by
 * {@link DAMAGE_SCALE} (~3.3x, integers). Mana costs, board slots, deck size and
 * the timers are pacing/economy levers, NOT damage math, so they are left at
 * their PRD values. The AI-vs-AI balance harness (`cardBalance.test.ts`) is the
 * acceptance gate that validates the chosen factor.
 *
 * This module is pure sim data + pure functions (no wall clock, no DOM, no Pixi),
 * so it lives inside the `architecture.test.ts` determinism boundary like the
 * rest of `sim/`.
 */
import { ARENA_STAGES, ARENA_ELITE_SEQUENCE } from './arena';
import { monsters, SLAYER_GROUPS } from './content';
import type { SlayerGroup } from './content';
export { SLAYER_GROUPS };
export type { SlayerGroup };

// ---------------------------------------------------------------------------
// Ruleset constants (PRD #493 "Battle ruleset", with the captain's 100-HP HP).
// The pacing/economy levers stay at their PRD values; only the damage economy is
// rescaled for 100-HP heroes (see DAMAGE_SCALE below).
// ---------------------------------------------------------------------------

/** Hero starting (and maximum) health. Captain's override: 30 -> 100. */
export const HERO_START_HP = 100;
/** Mana each hero starts their first turn with. */
export const START_MANA = 1;
/** Mana cap - a hero never has more than ten crystals. */
export const MAX_MANA = 10;
/** Board slots per side; a sixth minion cannot be summoned. */
export const BOARD_SLOTS = 5;
/** Cards in a legal deck - exactly this many. */
export const DECK_SIZE = 20;
/** At most this many copies of a card **name** (regardless of rarity) per deck. */
export const MAX_COPIES_PER_NAME = 2;
/** Cards held before excess draws are burned (the classic hand cap). */
export const HAND_LIMIT = 10;
/** Turn timer, in seconds - modelled as a `Timeout` command; the clock is the caller's. */
export const TURN_TIMER_SECONDS = 60;
/** Consecutive timeouts that forfeit the match. */
export const TIMEOUTS_TO_FORFEIT = 2;

/**
 * The damage-economy scale factor for 100-HP heroes. The stat formula is authored
 * at its natural 30-HP-design values and multiplied by
 * this so a match at 100 HP runs to the same turn count as the PRD's 30-HP design
 * (heroes are ~3.33x tougher, so minions hit ~3.3x harder). Validated by the
 * balance harness; if the harness ever demands a different factor, retune here -
 * this is the single knob for the whole card game's damage math.
 */
export const DAMAGE_SCALE = 3.3;

/** Baseline health offset applied by the shared 100-HP stat formula. */
export const BASE_HEALTH_OFFSET = Math.round(1 * DAMAGE_SCALE); // 3

/** Fatigue damage per empty-deck draw is `fatigueCount * FATIGUE_STEP` (scaled so decked-out games still resolve). */
export const FATIGUE_STEP = Math.round(1 * DAMAGE_SCALE); // 3

// ---------------------------------------------------------------------------
// Rarities and keywords.
// ---------------------------------------------------------------------------

/** The four rarities every card name exists in. */
export type CardRarity = 'common' | 'uncommon' | 'rare' | 'legendary';
export const CARD_RARITIES: readonly CardRarity[] = ['common', 'uncommon', 'rare', 'legendary'];

/** Stable identifiers for released card pools. */
export type CardSetId = 'core-v1' | 'core-v2' | 'core-v3';

/**
 * The identity-wave keyword vocabulary (launch pair + mechanics wave 1, PRD #588).
 * Each keyword has one exact rules sentence in {@link cardKeywordText}, shared by
 * card faces, battlefield tooltips, and rules tests so authored mechanics cannot
 * drift from the UI.
 */
export type CardKeyword =
  | 'taunt'
  | 'charge'
  | 'poison'
  | 'drain'
  | 'protection'
  | 'ranged'
  | 'fury'
  | 'stealth'
  | 'overwhelm'
  | 'heal';

/** Every keyword id, as a runtime list (the wire sends keywords as plain strings). */
export const CARD_KEYWORDS: readonly CardKeyword[] = [
  'taunt', 'charge', 'poison', 'drain', 'protection',
  'ranged', 'fury', 'stealth', 'overwhelm', 'heal',
];

/** Exact player-facing rules sentence for one keyword. */
export function cardKeywordText(keyword: CardKeyword, healAmount = 1): string {
  switch (keyword) {
    case 'taunt': return 'Enemies must attack this minion first.';
    case 'charge': return 'Can attack the turn it is played.';
    case 'poison': return 'Any minion this minion damages is destroyed.';
    case 'drain': return 'Damage this minion deals also heals your hero.';
    case 'protection': return 'The first time this minion would take damage, it takes none.';
    case 'ranged': return 'The defender does not strike back when this minion attacks.';
    case 'fury': return 'Can attack twice each turn.';
    case 'stealth': return 'Cannot be attacked or targeted until it attacks or its abilities deal hostile damage.';
    case 'overwhelm': return 'When this minion kills a defending minion, leftover damage hits the enemy hero.';
    case 'heal': return `At the end of your turn, this minion heals ${healAmount} of its own health.`;
  }
}

/** Rules text for one keyword id off the wire (unknown ids yield undefined). */
export function keywordRule(keyword: string, healAmount?: number): string | undefined {
  return (CARD_KEYWORDS as readonly string[]).includes(keyword)
    ? cardKeywordText(keyword as CardKeyword, healAmount)
    : undefined;
}

/** The bounded, data-authored vocabulary used by monster triggers. */
export type ScriptedCardEffect =
  ({
    kind: 'damage-enemy-hero'; amount: number
  }
  | {
    kind: 'damage-all-enemies'; amount: number
  }
  | {
    kind: 'heal-hero'; amount: number
  }
  | {
    /** Add hero health without the ordinary maximum-health clamp. */
    kind: 'gain-hero-health'; amount: number
  }
  | {
    kind: 'magic-reflection'
  }
  | {
    kind: 'draw'; amount: number
  }
  | {
    kind: 'buff-other-slayer-group'; slayerGroup: SlayerGroup; attack: number; health: number
  }
  | {
    kind: 'summon'; cardId: string; count?: number
  }
  | {
    kind: 'destroy-low-attack'; maximumAttack: number
  }
  | {
    kind: 'resurrect'; slayerGroup?: SlayerGroup
  }
  ) & {
    /** Optional player choice for an on-play effect; ignored for triggered timings. */
    target?: CardTargetDescriptor;
  };

/** Target contract for a scripted trigger; only on-play effects may ask a player. */
export function scriptedEffectTargetDescriptor(
  effect: ScriptedCardEffect,
  timing: keyof Pick<CardAbilities, 'onPlay' | 'onDeath' | 'endOfTurn'>,
): CardTargetDescriptor {
  if (timing !== 'onPlay') return { kind: 'none' };
  if (effect.target) return effect.target;
  switch (effect.kind) {
    case 'destroy-low-attack':
      return { kind: 'enemy-minion', maximumAttack: effect.maximumAttack };
    default:
      return { kind: 'none' };
  }
}

/** Player-facing rules text for one bounded scripted effect. */
export function scriptedEffectText(effect: ScriptedCardEffect): string {
  switch (effect.kind) {
    case 'damage-enemy-hero': return `Deal ${effect.amount} damage to the enemy hero`;
    case 'damage-all-enemies': return `Deal ${effect.amount} damage to all enemy minions`;
    case 'heal-hero': return `Restore ${effect.amount} health to your hero`;
    case 'gain-hero-health': return `Gain ${effect.amount} Health (this can exceed your maximum)`;
    case 'magic-reflection': return 'Cast Magic Reflection on your hero';
    case 'draw': return `Draw ${effect.amount} card${effect.amount === 1 ? '' : 's'}`;
    case 'buff-other-slayer-group': return `Give your other ${effect.slayerGroup} minions +${effect.attack}/+${effect.health}`;
    case 'summon': return `Summon ${effect.count ?? 1} ${effect.cardId.split('-').join(' ')}`;
    case 'destroy-low-attack': return `Destroy an enemy with ${effect.maximumAttack} or less attack`;
    case 'resurrect': return effect.slayerGroup ? `Return your latest valid fallen ${effect.slayerGroup}` : 'Return your latest valid fallen minion';
  }
}

export interface CardAbilities {
  onPlay?: ScriptedCardEffect;
  onDeath?: ScriptedCardEffect;
  endOfTurn?: ScriptedCardEffect;
  /** Magnitude for the Heal keyword. */
  heal?: number;
}

// ---------------------------------------------------------------------------
// Mana tier derivation (the "formula over the bestiary's tier groupings").
// ---------------------------------------------------------------------------

/**
 * Monster id -> mana cost, derived from the arena's canonical weak->strong
 * tables. The eight wave stages map to mana 1..8 (critters at 1, the endgame
 * dragonkin at 8); the twelve ordered elites climb from 8 to 10. The first table
 * to place a monster wins. Bosses and the two critter outliers the arena never
 * spawns are placed by {@link CARD_TWEAKS}; anything in neither falls to
 * {@link CARD_DEFAULT_MANA}.
 */
const MANA_BY_MONSTER: ReadonlyMap<string, number> = (() => {
  const m = new Map<string, number>();
  ARENA_STAGES.forEach((stage, i) => {
    const mana = Math.min(MAX_MANA, i + 1); // stage 0..7 -> mana 1..8
    for (const id of stage.monsters) if (!m.has(id)) m.set(id, mana);
  });
  ARENA_ELITE_SEQUENCE.forEach((elite, i) => {
    const mana = Math.min(MAX_MANA, 8 + Math.floor(i / 4)); // 8,8,8,8, 9,9,9,9, 10,10,10,10
    if (!m.has(elite.monster)) m.set(elite.monster, mana);
  });
  return m;
})();

/** Mana cost for a monster the arena tiers and tweak table do not place. */
export const CARD_DEFAULT_MANA = 4;

/**
 * The small hand-tweak table for outliers (PRD: "a hand-tweak table for
 * outliers"). It overrides cost, the attack/health baseline, and
 * grants keyword flavour for the handful of cards the tier formula alone gets
 * bland or wrong. Everything absent from this table is fully derived.
 *
 * Cost overrides cover the creatures the arena never spawns (slime/snake are
 * dropped from the wave tiers as hard-to-read sprites; the named bosses sit
 * outside every tier) so they still land at a sensible mana. Keywords give
 * fast strikers Charge and heavy bruisers Taunt so decks have shape.
 */
export interface CardTweak {
  /** Override the derived mana cost. */
  cost?: number;
  /** Override the derived attack. */
  attack?: number;
  /** Override the derived health. */
  health?: number;
  /** Keywords the card carries at every rarity. */
  keywords?: CardKeyword[];
  abilities?: CardAbilities;
}

/**
 * Central stat-tax tunables for authored mechanics.
 * Explicit stat overrides remain final; formula bodies pay these small health
 * taxes so balance adjustments stay data edits in one operator-facing block.
 */
export const CARD_STAT_TAXES = {
  healthPerKeyword: 1,
  healthOnPlay: 2,
  healthOnDeath: 1,
  healthEndOfTurn: 2,
} as const;

/** Stable cosmetic hue per rarity for creatures whose UO constructors vary naturally. */
const CARD_HUES_BY_RARITY: Readonly<Partial<Record<string, Readonly<Record<CardRarity, number>>>>> = {
  slime: { common: 2208, uncommon: 2224, rare: 2201, legendary: 2216 },
  snake: { common: 2006, uncommon: 2001, rare: 2012, legendary: 2018 },
  'headless-one': { common: 1002, uncommon: 1020, rare: 1040, legendary: 1058 },
};

export const CARD_TWEAKS: Readonly<Record<string, CardTweak>> = {
  // --- Keyword texture: the PRD #660 redistribution wave (ADR-0340). ---
  // Keywords are identity-first and UO-semantic: each assignment below must sit
  // inside its keyword's eligibility allowlist (pinned in cardTexture.test.ts),
  // and the per-keyword counts must land inside the banded texture budget the
  // same test enforces. Change texture only with that test and the balance gate
  // (cardBalance.test.ts) open; the ADR lists every changed card's rationale.
  //
  // Critter outliers the arena wave tiers deliberately omit (ADR-0211). A slime
  // is a plain soft blob (its old Taunt violated the wall-and-guardian rule);
  // a snake is a cheap venomous chip attacker.
  slime: { cost: 1 },
  snake: { cost: 1, attack: 2, keywords: ['poison'] },
  // Mounts, coursers, and pouncing beasts get Charge - they hit the turn they
  // land. Flying scouts (mongbat, harpy) and summoned constructs lost it.
  'dire-wolf': { keywords: ['charge'] },
  'hell-hound': { keywords: ['charge'] },
  wyvern: { keywords: ['charge'] },
  drake: { keywords: ['charge'] },
  // Venomous creatures carry Poison: the imp poisons in UO lore. Cheap poison
  // pays in attack (the snake/frost-spider chip-attacker rail).
  imp: { attack: 2, keywords: ['poison'] },
  // Living stone shrugs the first blow (Protection).
  'stone-harpy': { keywords: ['protection'] },
  'stone-gargoyle': { keywords: ['protection'] },
  'earth-elemental': { keywords: ['protection'] },
  // Hulking blockers and rooted things guard the board with Taunt.
  ettin: { keywords: ['taunt'] },
  ogre: { keywords: ['taunt'] },
  'ogre-lord': { keywords: ['taunt'] },
  'arctic-ogre-lord': { keywords: ['taunt'] },
  troll: { keywords: ['taunt'] },
  cyclops: { keywords: ['taunt'] },
  titan: { keywords: ['taunt'] },
  corpser: { keywords: ['taunt'] },
  'orc-captain': { keywords: ['taunt'] },
  'skeletal-knight': { keywords: ['taunt'] },
  // Casters and archers whose art reads at distance attack without retaliation.
  gazer: { keywords: ['ranged'] },
  'ratman-archer': { keywords: ['ranged'] },
  'ophidian-mage': { keywords: ['ranged'] },
  // Necromantic and vampiric identities leech life (Drain).
  spirit: { keywords: ['drain'] },
  'blood-elemental': { keywords: ['drain'] },
  // Daemon-line frenzy (Fury).
  daemon: { keywords: ['fury'] },
  // Massive wyrms carry their blows through the defender (Overwhelm).
  'shadow-wyrm': { keywords: ['overwhelm'] },
  // Card-only classic creatures use an explicit curve because they deliberately
  // have no Arena tier from which to derive mana.
  alligator: { cost: 2 },
  'black-bear': { cost: 2 },
  'brown-bear': { cost: 3 },
  'grizzly-bear': { cost: 4, keywords: ['fury'] },
  'polar-bear': { cost: 4 },
  // The stalking cat is the pool's beast ambusher (Stealth, ADR-0340); its
  // bigger cousin below keeps the pounce (Charge).
  cougar: { cost: 2, keywords: ['stealth'] },
  // Panther moved 3 -> 4 mana with cost-appropriate attack (ADR-0331 seat-
  // fairness curve tuning).
  panther: { cost: 4, attack: 14, keywords: ['charge'] },
  'giant-rat': { cost: 1, keywords: ['charge', 'fury'] },
  'giant-toad': { cost: 2 },
  'giant-serpent': { cost: 3, attack: 2, keywords: ['poison'] },
  'frost-spider': { cost: 3, attack: 2, keywords: ['poison', 'stealth'] },
  // The classic UO regenerator: Heal replaced its Taunt (its plain twin keeps
  // the wall role).
  'frost-troll': { cost: 5, keywords: ['heal'], abilities: { heal: 3 } },
  'lava-lizard': { cost: 4 },
  nightmare: { cost: 8, keywords: ['charge', 'overwhelm'] },
  // Forest ostard moved 2 -> 3 mana with cost-appropriate attack (ADR-0331);
  // its desert twin stays at 2, so the pair now offers a real curve tradeoff.
  'forest-ostard': { cost: 3, attack: 11, keywords: ['charge'] },
  'desert-ostard': { cost: 2, keywords: ['charge'] },
  // Frenzied is in the name: the war-mount pairs Charge with Fury.
  'frenzied-ostard': { cost: 4, keywords: ['charge', 'fury'] },
  scorpion: { attack: 2, keywords: ['poison'] },
  'elder-gazer': { cost: 6 },
  wisp: { cost: 4, keywords: ['ranged', 'protection'] },
  'swamp-tentacle': { cost: 4, keywords: ['taunt'] },
  'orcish-lord': { cost: 5 },
  'orcish-mage': { cost: 4, keywords: ['ranged'] },
  'terathan-matriarch': { cost: 7, abilities: { onPlay: { kind: 'buff-other-slayer-group', slayerGroup: 'arachnid', attack: 2, health: 2 } } },
  'ophidian-knight-errant': { cost: 6, keywords: ['taunt'] },
  'ophidian-matriarch': { cost: 7 },
  'ice-elemental': { cost: 5, keywords: ['protection'] },
  // The flagship Poison body (its old Drain violated the necromantic rule).
  'poison-elemental': { cost: 8, keywords: ['poison'] },
  'snow-elemental': { cost: 6 },
  'sea-serpent': { cost: 5 },
  'deep-sea-serpent': { cost: 7 },
  kraken: { cost: 8, keywords: ['overwhelm'] },
  // A blade spirit attacks whatever is nearby the instant it is summoned in UO,
  // and the Blade Spirits spell's summon is built on that immediacy - it keeps
  // Charge through the texture wave (allowlisted in cardTexture.test.ts).
  'blade-spirit': { cost: 5, keywords: ['charge'] },
  'energy-vortex': { cost: 7 },
  // Core Set v3: explicit stat/effect packages approved as a single identity wave.
  pixie: { cost: 2, attack: 5, health: 10, abilities: { onPlay: { kind: 'gain-hero-health', amount: 10 } } },
  'treefellow-guardian': {
    cost: 3,
    attack: 7,
    health: 10,
    keywords: ['taunt'],
    abilities: {
      onPlay: { kind: 'gain-hero-health', amount: 10 },
      onDeath: { kind: 'gain-hero-health', amount: 10 },
    },
  },
  'copper-elemental': { cost: 4, attack: 13, health: 14, abilities: { onPlay: { kind: 'magic-reflection' } } },
  phoenix: { cost: 7, attack: 22, health: 22, abilities: { onPlay: { kind: 'damage-all-enemies', amount: 12 } } },
  // Seat-fairness curve tuning (PRD #638 Slice 3, ADR-0331). The reference
  // mirrors' starting player was winning 56-68%; the residual driver (after the
  // [2, 4] opening-hand change) was ultra-cheap curves that let the second
  // player's extra cards convert into tempo too freely in some mirrors and not
  // at all in others. These cards moved one rung up the curve with
  // cost-appropriate stats (power-neutral for the pairwise band, slower to
  // deploy), landing every reference mirror's starting-seat rate in 45-55%
  // over the harness's fixed seeds. Retune only with `cardBalance.test.ts` open.
  'giant-spider': { cost: 2, attack: 7 },
  reaper: { cost: 3 },
  lizardman: { cost: 5 },
  'dread-spider': { cost: 3, attack: 11, health: 14 },
  // Arachnid pressure comes from cheap bodies, swarm buffs, and sticky death triggers.
  'terathan-warrior': { keywords: ['poison'] },
  ratman: { abilities: { onPlay: { kind: 'buff-other-slayer-group', slayerGroup: 'repond', attack: 1, health: 1 } } },
  'ratman-mage': { abilities: { onPlay: { kind: 'damage-enemy-hero', amount: 5 } } },
  'terathan-avenger': {
    abilities: { onDeath: { kind: 'summon', cardId: 'terathan-drone' } },
  },
  // Undead trade repeatedly and turn their graveyard into a resource.
  zombie: { keywords: ['taunt'], abilities: { onDeath: { kind: 'summon', cardId: 'skeleton' } } },
  skeleton: { abilities: { onDeath: { kind: 'damage-enemy-hero', amount: 6 } } },
  'skeleton-warrior': { health: 15, keywords: ['protection'] },
  ghoul: { keywords: ['stealth', 'drain'], abilities: { endOfTurn: { kind: 'damage-enemy-hero', amount: 8 } } },
  'rotting-corpse': { cost: 3, abilities: { onDeath: { kind: 'damage-enemy-hero', amount: 12 } } },
  mummy: { keywords: ['heal'], abilities: { heal: 3 } },
  'bone-magi': { abilities: { onPlay: { kind: 'damage-enemy-hero', amount: 6 } } },
  lich: { keywords: ['drain'], abilities: { onPlay: { kind: 'draw', amount: 1 } } },
  'lich-lord': { keywords: ['drain'], abilities: { endOfTurn: { kind: 'damage-enemy-hero', amount: 4 } } },
  'patchwork-skeleton': { abilities: { onDeath: { kind: 'resurrect', slayerGroup: 'undead' } } },
  // Necromantic stitching knits the golem back together at end of turn.
  'flesh-golem': { keywords: ['heal'], abilities: { heal: 4 } },
  // Demons are expensive finishers that repay surviving to the late game.
  'gate-daemon': { abilities: { onPlay: { kind: 'summon', cardId: 'imp' } } },
  gibberling: { cost: 1 },
  'mound-of-maggots': { cost: 2, abilities: { onDeath: { kind: 'summon', cardId: 'imp' } } },
  // Gore fiend's attack came down 10 -> 8 (ADR-0331): at 10 the Demon deck
  // beat other reference decks past the 60% pairwise band; at 7 it fell too
  // far the other way against Starter. 8 centres every Demon pairing. Its
  // Drain became Fury in the redistribution wave (the PRD's frenzied-gore
  // identity; Drain is reserved for necromantic leeches - ADR-0340).
  'gore-fiend': { cost: 3, attack: 8, keywords: ['fury'] },
  // Dragons are the aggressive top of the curve - bias toward attack.
  dragon: { attack: scaledStat(8) + 4 },
  'ancient-wyrm': { attack: scaledStat(8) + 6, keywords: ['overwhelm'] },
  // Named bosses lean to the top of the curve as the chase cards (PRD). They
  // still exist in all four rarities; {@link CHASE_MONSTERS} marks them for the
  // pack-weighting seam so they surface as rare/legendary pulls.
  'demon-lord': { cost: 10, keywords: ['overwhelm'], abilities: { onPlay: { kind: 'buff-other-slayer-group', slayerGroup: 'demon', attack: 2, health: 2 } } },
  'demon-knight': { cost: 10, abilities: { onPlay: {
    kind: 'destroy-low-attack',
    maximumAttack: 10,
    target: { kind: 'enemy-minion', maximumAttack: 10 },
  } } },
  'demon-knight-boss': { cost: 10, abilities: { onDeath: { kind: 'summon', cardId: 'demon-knight' } } },
  'darknight-creeper': { cost: 10, keywords: ['stealth'], abilities: { endOfTurn: { kind: 'damage-enemy-hero', amount: 5 } } },
  'abysmal-horror': { cost: 9, abilities: { onPlay: { kind: 'damage-all-enemies', amount: 5 } } },
  balron: { cost: 10, keywords: ['fury'], abilities: { onPlay: { kind: 'damage-enemy-hero', amount: 8 } } },
  rikktor: { cost: 10, keywords: ['overwhelm'], abilities: { onPlay: { kind: 'damage-all-enemies', amount: 6 } } },
  mephitis: { cost: 10, abilities: { onPlay: {
    kind: 'destroy-low-attack',
    maximumAttack: 8,
    target: { kind: 'enemy-minion', maximumAttack: 8 },
  } } },
  semidar: { cost: 10, keywords: ['drain', 'fury'], abilities: { onPlay: { kind: 'heal-hero', amount: 15 } } },
};

/**
 * The chase legendaries (PRD: "Named bosses ... lean legendary as the chase
 * cards"). A pure set the pack-roll seam ({@link ../sim/cardPacks}) reads to
 * bias these names toward rare/legendary appearances; the battle engine and the
 * card definitions treat them like any other card (all four rarities exist).
 */
export const CHASE_MONSTERS: ReadonlySet<string> = new Set([
  'demon-lord',
  'demon-knight-boss',
  'darknight-creeper',
  'abysmal-horror',
  'balron',
  'rikktor',
  'mephitis',
  'semidar',
]);

/**
 * Every card identity reserved for rare and legendary pack pulls.
 *
 * The original eight boss monsters remain chase cards. Core Set v3 adds two
 * showcase creatures and two singular spells without making their
 * lower-rarity definitions play differently.
 */
export const CHASE_CARDS: ReadonlySet<string> = new Set([
  ...CHASE_MONSTERS,
  'phoenix',
  'copper-elemental',
  'blood-oath',
  'word-of-death',
]);

/**
 * The monster stat formula, at the 30-HP design value, scaled for 100-HP heroes.
 * Attack tracks mana (a 3-cost is roughly a 3-attack at 30 HP); health is one
 * higher (a 3-cost is roughly 3/4). Both times {@link DAMAGE_SCALE}, floored at 1.
 */
export function scaledStat(cost: number): number {
  return Math.max(1, Math.round(cost * DAMAGE_SCALE));
}

/** Derived mana cost for a monster card (tweak override, else tier, else default). */
export function monsterCardCost(id: string): number {
  const tweak = CARD_TWEAKS[id];
  if (tweak?.cost != null) return tweak.cost;
  return MANA_BY_MONSTER.get(id) ?? CARD_DEFAULT_MANA;
}

/** Common-rarity attack/health for a monster card (tweak overrides, else the formula). */
export function monsterCommonStats(id: string): { attack: number; health: number } {
  const cost = monsterCardCost(id);
  const tweak = CARD_TWEAKS[id];
  const abilities = tweak?.abilities;
  const healthTax = (tweak?.keywords?.length ?? 0) * CARD_STAT_TAXES.healthPerKeyword
    + (abilities?.onPlay ? CARD_STAT_TAXES.healthOnPlay : 0)
    + (abilities?.onDeath ? CARD_STAT_TAXES.healthOnDeath : 0)
    + (abilities?.endOfTurn || abilities?.heal ? CARD_STAT_TAXES.healthEndOfTurn : 0);
  return {
    attack: tweak?.attack ?? scaledStat(cost),
    health: tweak?.health ?? Math.max(1, scaledStat(cost) + BASE_HEALTH_OFFSET - healthTax),
  };
}

// ---------------------------------------------------------------------------
// Card definitions.
// ---------------------------------------------------------------------------

/** The kind discriminator shared by every card shape. */
export type CardKind = 'monster' | 'spell' | 'item';

/** A monster (minion) card at a concrete rarity. */
export interface MonsterCardDef {
  kind: 'monster';
  setId: CardSetId;
  /** Content id (the bestiary monster id). */
  cardId: string;
  /** Display name (the bestiary name). */
  name: string;
  rarity: CardRarity;
  cost: number;
  attack: number;
  health: number;
  keywords: CardKeyword[];
  /** Primary UO super-slayer group, or null when the creature has no such vulnerability. */
  slayerGroup: SlayerGroup | null;
  abilities: CardAbilities;
  /** UO body id, for the later sprite-in-frame card renderer. */
  body: number;
  /** Canonical encoded UO hue from the shared bestiary. */
  hue?: number;
}

/** What a one-shot spell or item card does when it resolves. */
export type ActionEffectKind =
  | 'heal-hero'
  | 'heal-friendly'
  | 'damage-any'
  | 'damage-all-enemies'
  | 'damage-chain'
  | 'delayed-damage-any'
  | 'draw'
  | 'buff-friendly'
  | 'return-friendly'
  | 'break-paralyze'
  | 'paralyze'
  | 'reactive-armor'
  | 'magic-reflection'
  | 'damage-all-minions'
  | 'resurrect'
  | 'animate-dead'
  | 'summon'
  | 'gain-mana'
  | 'blood-oath'
  | 'heal-all-friendly'
  | 'destroy-minion'
  | 'grant-fury';

/** The resolved behavior of a spell or item card at a concrete rarity. */
export interface ActionEffect {
  kind: ActionEffectKind;
  /** Heal/damage points, draw count, or the attack half of a buff. */
  amount: number;
  /** Owner turns to wait before a summoned minion enters play. */
  summonDelayTurns?: number;
  /** The health half of a `buff-friendly` effect (absent otherwise). */
  buffHealth?: number;
  /** Damage dealt to secondary targets by Chain Lightning. */
  secondaryAmount?: number;
  /** Maximum number of targets struck by a chained effect. */
  maxTargets?: number;
  /** Divide `amount` across all affected targets instead of applying it to each. */
  split?: true;
  /** Summoned card for a summon spell. */
  cardId?: string;
  /** Equal-weighted summon pool; one id is rolled when the action resolves. */
  cardIds?: readonly string[];
}

/** The bounded classes of player-selected card targets. */
export type CardTargetClass =
  | 'none'
  | 'friendly-hero'
  | 'enemy-hero'
  | 'any-hero'
  | 'friendly-minion'
  | 'enemy-minion'
  | 'any-minion'
  | 'friendly-character'
  | 'enemy-character'
  | 'any-character'
  | 'friendly-graveyard-minion';

/**
 * One authored target contract. Predicates are deliberately data, so the
 * engine, AI, directed wire state, and card text cannot grow card-id branches.
 */
export interface CardTargetDescriptor {
  kind: CardTargetClass;
  maximumAttack?: number;
  slayerGroup?: SlayerGroup;
  damaged?: boolean;
  paralyzed?: boolean;
}

/** The target contract for a spell or item effect. */
export function actionTargetDescriptor(effect: ActionEffect): CardTargetDescriptor {
  switch (effect.kind) {
    case 'damage-any': return { kind: 'any-character' };
    case 'damage-chain':
    case 'delayed-damage-any':
      return { kind: 'enemy-character' };
    case 'heal-friendly':
      return { kind: 'friendly-character' };
    case 'buff-friendly':
    case 'return-friendly':
      return { kind: 'friendly-minion' };
    case 'break-paralyze':
      return { kind: 'friendly-minion', paralyzed: true };
    case 'paralyze':
      return { kind: 'enemy-minion', paralyzed: false };
    case 'resurrect':
    case 'animate-dead':
      return { kind: 'friendly-graveyard-minion' };
    case 'destroy-minion':
      return { kind: 'any-minion' };
    case 'grant-fury':
      return { kind: 'friendly-minion' };
    default:
      return { kind: 'none' };
  }
}

export type SpellSchool = 'magery' | 'necromancy' | 'chivalry' | 'spellweaving';
export type ItemType = 'potion' | 'pouch' | 'spellbook' | 'token';
export type ActionCategory = 'heal' | 'damage' | 'removal' | 'utility';

interface ActionCardFields {
  setId: CardSetId;
  cardId: string;
  name: string;
  rarity: CardRarity;
  cost: number;
  effect: ActionEffect;
  /** Coarse role, for deck-builder filtering later. */
  category: ActionCategory;
  /** Concise canonical UO behavior that the TCG adaptation is based on. */
  uoDescription: string;
}

/** A canonical UO spell adapted into one TCG action. */
export interface SpellCardDef extends ActionCardFields {
  kind: 'spell';
  school: SpellSchool;
  /** Magery circle. Other schools do not use circles. */
  circle?: number;
}

/** A canonical UO inventory item adapted into one TCG action. */
export interface ItemCardDef extends ActionCardFields {
  kind: 'item';
  itemType: ItemType;
}

export type ActionCardDef = SpellCardDef | ItemCardDef;
export type CardDef = MonsterCardDef | ActionCardDef;

/** Human-facing TCG rules text for one spell or item card. */
export function actionCardDescription(def: ActionCardDef): string {
  const effect = def.effect;
  switch (effect.kind) {
    case 'heal-hero':
      return `Restore ${effect.amount} health`;
    case 'heal-friendly':
      return `Restore ${effect.amount} health to your hero or a friendly minion`;
    case 'damage-any':
      return `Deal ${effect.amount} damage`;
    case 'damage-all-enemies':
      return effect.split
        ? `Deal ${effect.amount} damage among enemy minions, concentrated to destroy as many as possible`
        : `Deal ${effect.amount} damage to all enemy minions`;
    case 'damage-all-minions':
      return `Deal ${effect.amount} to every minion`;
    case 'damage-chain':
      return `Deal ${effect.amount} damage, then ${effect.secondaryAmount ?? effect.amount} to up to ${(effect.maxTargets ?? 1) - 1} other enemies`;
    case 'delayed-damage-any':
      return `Deal ${effect.amount} damage after your opponent's next turn`;
    case 'draw':
      return `Draw ${effect.amount} card${effect.amount === 1 ? '' : 's'}`;
    case 'buff-friendly':
      return `Give +${effect.amount}/+${effect.buffHealth ?? 0}`;
    case 'return-friendly':
      return 'Return a friendly minion to your hand';
    case 'break-paralyze':
      return `Deal ${effect.amount} damage to a friendly Paralyzed minion and free it`;
    case 'paralyze':
      return 'Paralyze an enemy minion until damage breaks it or its turn ends';
    case 'reactive-armor':
      return `Prevent the next ${effect.amount} damage to your hero`;
    case 'magic-reflection':
      return 'Reflect the next enemy damage spell targeting your hero';
    case 'resurrect':
      return 'Choose a fallen minion to return';
    case 'animate-dead':
      return `Choose a fallen minion to consume and summon a ${effect.cardId ? titleCardId(effect.cardId) : 'Skeleton'}`;
    case 'summon':
      if (effect.cardIds?.length) {
        return `Summon one random creature: ${formatCardIdList(effect.cardIds)}`;
      }
      return effect.summonDelayTurns
        ? `At the start of your next turn, summon ${effect.amount} ${effect.cardId ? titleCardId(effect.cardId) : 'minion'}${effect.amount === 1 ? '' : 's'}. It can't attack that turn`
        : `Summon ${effect.amount} ${effect.cardId ? titleCardId(effect.cardId) : 'minion'}${effect.amount === 1 ? '' : 's'}`;
    case 'gain-mana':
      return `Gain ${effect.amount} mana this turn only`;
    case 'blood-oath':
      return 'Until the start of your next turn, damage to either hero is also dealt to the other';
    case 'heal-all-friendly':
      return 'Restore all friendly minions to full Health. Your hero takes half the Health restored, rounded up';
    case 'destroy-minion':
      return 'Destroy a minion';
    case 'grant-fury':
      return 'Give a friendly minion Fury this turn';
  }
}

function titleCardId(cardId: string): string {
  return cardId
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatCardIdList(cardIds: readonly string[]): string {
  const names = cardIds.map(titleCardId);
  if (names.length < 2) return names[0] ?? 'minion';
  return `${names.slice(0, -1).join(', ')}, or ${names.at(-1)}`;
}

/**
 * The hand-authored UO spell and item catalog spanning healing, damage, control,
 * summoning, resurrection, and defensive wards.
 * Amounts are the Core Set v2 magnitudes, already scaled for 100-HP heroes.
 * Rarity never changes gameplay values, so neither costs nor effects change
 * between printings even when pack availability differs by identity.
 */
type ActionBase = {
  cardId: string;
  name: string;
  cost: number;
  category: ActionCategory;
  effectKind: ActionEffectKind;
  amount: number;
  summonDelayTurns?: number;
  buffHealth?: number;
  secondaryAmount?: number;
  maxTargets?: number;
  split?: true;
  cardIdToSummon?: string;
  cardIdsToSummon?: readonly string[];
  uoDescription: string;
} & (
  | { kind: 'spell'; school: SpellSchool; circle?: number }
  | { kind: 'item'; itemType: ItemType }
);

/** Equal-weighted outcomes for the Magery spell Summon Creature. */
export const SUMMON_CREATURE_POOL: readonly string[] = Object.freeze([
  'dire-wolf',
  'brown-bear',
  'forest-ostard',
  'panther',
  'grizzly-bear',
]);

// ADR-0331 (seat-fairness/pairwise tuning) touched three actions: Fireball
// 3 mana/13 -> 4 mana/16 (Starter curve raise), Explosion 1 -> 2 mana (its
// 1-mana printing made the second player's extra cards over-compensate in the
// Arachnid mirror), Summon Daemon 6 -> 5 mana (Demon mirror seat balance).
// The direct-damage spell ladder later restored Fireball's circle-3 cost and
// aligned all four spells by circle and power: Fireball < Lightning < Energy
// Bolt < Flame Strike. ADR-0375 caps any ten-mana combination at 50 damage.
// ADR-0355 made Summon Daemon a five-mana delayed summon; ADR-0373 corrected
// the arrival to have ordinary summoning sickness after it materializes.
const ACTION_BASE: readonly ActionBase[] = [
  { cardId: 'healing-potion', name: 'Heal Potion', kind: 'item', itemType: 'potion', cost: 2, category: 'heal', effectKind: 'heal-hero', amount: 20, uoDescription: 'Restores the drinker\'s health.' },
  { cardId: 'greater-heal', name: 'Greater Heal', kind: 'spell', school: 'magery', circle: 4, cost: 4, category: 'heal', effectKind: 'heal-friendly', amount: 36, uoDescription: 'Restores a large amount of health to the target.' },
  { cardId: 'fireball', name: 'Fireball', kind: 'spell', school: 'magery', circle: 3, cost: 3, category: 'damage', effectKind: 'damage-any', amount: 15, uoDescription: 'Strikes one target with fire damage.' },
  { cardId: 'lightning', name: 'Lightning', kind: 'spell', school: 'magery', circle: 4, cost: 4, category: 'damage', effectKind: 'damage-any', amount: 20, uoDescription: 'Strikes one target with energy damage.' },
  { cardId: 'explosion-potion', name: 'Explosion Potion', kind: 'item', itemType: 'potion', cost: 4, category: 'removal', effectKind: 'damage-all-enemies', amount: 20, split: true, uoDescription: 'Explodes after being thrown and damages characters in an area.' },
  { cardId: 'trapped-pouch', name: 'Trapped Pouch', kind: 'item', itemType: 'pouch', cost: 1, category: 'utility', effectKind: 'break-paralyze', amount: 1, uoDescription: 'Inflicts a small amount of self-damage to break paralysis.' },
  { cardId: 'teleport', name: 'Teleport', kind: 'spell', school: 'magery', circle: 3, cost: 2, category: 'utility', effectKind: 'return-friendly', amount: 1, uoDescription: 'Moves the caster to a visible nearby location and breaks pursuit targeting.' },
  { cardId: 'spellbook', name: 'Spellbook', kind: 'item', itemType: 'spellbook', cost: 3, category: 'utility', effectKind: 'draw', amount: 2, uoDescription: 'Stores the spells a mage has learned.' },
  { cardId: 'bless', name: 'Bless', kind: 'spell', school: 'magery', circle: 3, cost: 2, category: 'utility', effectKind: 'buff-friendly', amount: 6, buffHealth: 6, uoDescription: 'Temporarily increases the target\'s strength, dexterity, and intelligence.' },
  { cardId: 'energy-bolt', name: 'Energy Bolt', kind: 'spell', school: 'magery', circle: 6, cost: 6, category: 'damage', effectKind: 'damage-any', amount: 30, uoDescription: 'Strikes one target with energy damage.' },
  { cardId: 'flame-strike', name: 'Flame Strike', kind: 'spell', school: 'magery', circle: 7, cost: 7, category: 'removal', effectKind: 'damage-any', amount: 35, uoDescription: 'Engulfs one target in a powerful column of fire.' },
  { cardId: 'chain-lightning', name: 'Chain Lightning', kind: 'spell', school: 'magery', circle: 7, cost: 5, category: 'removal', effectKind: 'damage-chain', amount: 11, secondaryAmount: 7, maxTargets: 3, uoDescription: 'Strikes a primary target and chains to nearby targets.' },
  { cardId: 'earthquake', name: 'Earthquake', kind: 'spell', school: 'magery', circle: 8, cost: 7, category: 'removal', effectKind: 'damage-all-minions', amount: 22, uoDescription: 'Damages nearby characters around the caster.' },
  { cardId: 'resurrection', name: 'Resurrection', kind: 'spell', school: 'magery', circle: 8, cost: 6, category: 'utility', effectKind: 'resurrect', amount: 1, uoDescription: 'Restores a ghost to life.' },
  { cardId: 'animate-dead', name: 'Animate Dead', kind: 'spell', school: 'necromancy', cost: 4, category: 'utility', effectKind: 'animate-dead', amount: 1, cardIdToSummon: 'skeleton', uoDescription: 'Consumes a corpse to create an uncontrolled undead creature.' },
  { cardId: 'blade-spirits', name: 'Blade Spirits', kind: 'spell', school: 'magery', circle: 5, cost: 5, category: 'utility', effectKind: 'summon', amount: 1, cardIdToSummon: 'blade-spirit', uoDescription: 'Summons a blade spirit that attacks nearby creatures.' },
  { cardId: 'summon-creature', name: 'Summon Creature', kind: 'spell', school: 'magery', circle: 5, cost: 3, category: 'utility', effectKind: 'summon', amount: 1, cardIdsToSummon: SUMMON_CREATURE_POOL, uoDescription: 'Summons a creature to serve the caster.' },
  { cardId: 'explosion', name: 'Explosion', kind: 'spell', school: 'magery', circle: 6, cost: 2, category: 'damage', effectKind: 'delayed-damage-any', amount: 18, uoDescription: 'Places a delayed magical explosion on one target.' },
  { cardId: 'vengeful-spirit', name: 'Vengeful Spirit', kind: 'spell', school: 'necromancy', cost: 4, category: 'utility', effectKind: 'summon', amount: 1, cardIdToSummon: 'spirit', uoDescription: 'Summons a revenant that relentlessly pursues its target.' },
  { cardId: 'summon-daemon', name: 'Summon Daemon', kind: 'spell', school: 'magery', circle: 8, cost: 5, category: 'utility', effectKind: 'summon', amount: 1, summonDelayTurns: 1, cardIdToSummon: 'daemon', uoDescription: 'Summons a daemon to serve the caster.' },
  { cardId: 'paralyze', name: 'Paralyze', kind: 'spell', school: 'magery', circle: 5, cost: 4, category: 'removal', effectKind: 'paralyze', amount: 1, uoDescription: 'Prevents the target from moving until the effect expires or damage breaks it.' },
  { cardId: 'reactive-armor', name: 'Reactive Armor', kind: 'spell', school: 'magery', circle: 1, cost: 2, category: 'utility', effectKind: 'reactive-armor', amount: 12, uoDescription: 'Raises physical resistance while lowering elemental resistances.' },
  { cardId: 'magic-reflection', name: 'Magic Reflection', kind: 'spell', school: 'magery', circle: 5, cost: 4, category: 'utility', effectKind: 'magic-reflection', amount: 0, uoDescription: 'Reflects hostile magic while trading away physical resistance.' },
  { cardId: 'blood-oath', name: 'Blood Oath', kind: 'spell', school: 'necromancy', cost: 3, category: 'damage', effectKind: 'blood-oath', amount: 0, uoDescription: 'Creates a dark pact that returns damage dealt by the target to the target.' },
  { cardId: 'noble-sacrifice', name: 'Noble Sacrifice', kind: 'spell', school: 'chivalry', cost: 3, category: 'heal', effectKind: 'heal-all-friendly', amount: 0, uoDescription: 'Heals nearby allies while reducing the caster to the brink of death.' },
  { cardId: 'word-of-death', name: 'Word of Death', kind: 'spell', school: 'spellweaving', cost: 5, category: 'removal', effectKind: 'destroy-minion', amount: 0, uoDescription: 'Slays a sufficiently wounded creature or deals chaos damage when it survives.' },
  { cardId: 'divine-fury', name: 'Divine Fury', kind: 'spell', school: 'chivalry', cost: 2, category: 'utility', effectKind: 'grant-fury', amount: 0, uoDescription: 'Briefly increases swing speed, hit chance, and damage at the cost of defense.' },
];

const ACTION_BY_ID: ReadonlyMap<string, ActionBase> = new Map(ACTION_BASE.map((e) => [e.cardId, e]));

/** Every current spell and item card id. */
export const ACTION_CARD_IDS: readonly string[] = ACTION_BASE.map((e) => e.cardId);

/** Frozen one-shot membership for historical Core Set v1. */
export const CORE_SET_V1_EQUIPMENT_IDS: readonly string[] = Object.freeze([
  'healing-potion',
  'greater-heal',
  'fireball',
  'lightning-bolt',
  'explosion-potion',
  'trapped-pouch',
  'teleport-scroll',
  'friends',
  'vigor',
  'energy-bolt',
  'flamestrike',
  'chain-lightning',
  'earthquake',
  'resurrection',
  'animate-dead',
  'blade-spirits',
  'call-of-the-pack',
  'pack-instinct',
  'skeleton-swarm',
  'unholy-summoning',
  'demon-pact',
  'reactive-armor',
  'magic-reflection',
]);

/** Frozen spell and item membership for the UO-authentic Core Set v2. */
export const CORE_SET_V2_ACTION_IDS: readonly string[] = Object.freeze([
  'healing-potion',
  'greater-heal',
  'fireball',
  'lightning',
  'explosion-potion',
  'trapped-pouch',
  'teleport',
  'spellbook',
  'bless',
  'energy-bolt',
  'flame-strike',
  'chain-lightning',
  'earthquake',
  'resurrection',
  'animate-dead',
  'blade-spirits',
  'summon-creature',
  'explosion',
  'vengeful-spirit',
  'summon-daemon',
  'paralyze',
  'reactive-armor',
  'magic-reflection',
]);

/** Core Set v3 retains the audited v2 catalog and adds four canonical UO spells. */
export const CORE_SET_V3_ACTION_IDS: readonly string[] = Object.freeze([
  ...CORE_SET_V2_ACTION_IDS,
  'blood-oath',
  'noble-sacrifice',
  'word-of-death',
  'divine-fury',
]);

/** Old card ids mapped to the canonical Core Set v2 identity that preserves ownership. */
export const LEGACY_CARD_ID_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  'lightning-bolt': 'lightning',
  'teleport-scroll': 'teleport',
  friends: 'spellbook',
  vigor: 'bless',
  flamestrike: 'flame-strike',
  'call-of-the-pack': 'summon-creature',
  'pack-instinct': 'explosion',
  'skeleton-swarm': 'vengeful-spirit',
  'unholy-summoning': 'summon-daemon',
  'demon-pact': 'paralyze',
});

/** Return the current identity for a persisted historical card id. */
export function canonicalCardId(id: string): string {
  return LEGACY_CARD_ID_ALIASES[id] ?? id;
}

/**
 * Rewrite historical card identities and merge collection rows that now name
 * the same concrete card printing.
 */
export function canonicalizeCardCollection(
  entries: readonly CardCollectionEntry[],
): CardCollectionEntry[] {
  const canonical: CardCollectionEntry[] = [];
  const byPrinting = new Map<string, CardCollectionEntry>();
  for (const entry of entries) {
    const cardId = canonicalCardId(entry.cardId);
    const key = `${cardId}\0${entry.rarity}`;
    const existing = byPrinting.get(key);
    if (existing) existing.count += entry.count;
    else {
      const migrated = { ...entry, cardId };
      byPrinting.set(key, migrated);
      canonical.push(migrated);
    }
  }
  return canonical;
}

/** Rewrite every historical card identity inside saved named decks. */
export function canonicalizeNamedDecks(decks: readonly NamedDeck[]): NamedDeck[] {
  let foundDefault = false;
  return decks.map((deck) => {
    const isDefault = deck.default === true && !foundDefault;
    if (isDefault) foundDefault = true;
    return {
      name: deck.name,
      cards: deck.cards.map((ref) => ({
        cardId: canonicalCardId(ref.cardId),
        rarity: ref.rarity,
      })),
      ...(isDefault ? { default: true } : {}),
    };
  });
}

/**
 * Frozen monster membership for Core Set v1.
 * Adding a creature to the bestiary does not silently make it legal card content.
 */
export const CORE_SET_V1_MONSTER_IDS: readonly string[] = Object.freeze([
  'slime',
  'snake',
  'mongbat',
  'giant-spider',
  'dread-spider',
  'scorpion',
  'dire-wolf',
  'hell-hound',
  'harpy',
  'stone-harpy',
  'imp',
  'corpser',
  'reaper',
  'spirit',
  'zombie',
  'skeleton',
  'skeleton-warrior',
  'ghoul',
  'skeletal-knight',
  'bone-magi',
  'rotting-corpse',
  'mummy',
  'orc',
  'orc-captain',
  'orc-warrior',
  'lizardman',
  'ratman',
  'ratman-archer',
  'ratman-mage',
  'ettin',
  'ogre',
  'ogre-lord',
  'troll',
  'gazer',
  'headless-one',
  'gargoyle',
  'stone-gargoyle',
  'cyclops',
  'lich',
  'lich-lord',
  'ophidian-mage',
  'ophidian-warrior',
  'blood-elemental',
  'air-elemental',
  'earth-elemental',
  'fire-elemental',
  'water-elemental',
  'terathan-warrior',
  'terathan-drone',
  'terathan-avenger',
  'efreet',
  'arctic-ogre-lord',
  'titan',
  'daemon',
  'gate-daemon',
  'ice-fiend',
  'wyvern',
  'drake',
  'dragon',
  'white-wyrm',
  'shadow-wyrm',
  'ancient-wyrm',
  'alligator',
  'black-bear',
  'brown-bear',
  'grizzly-bear',
  'polar-bear',
  'cougar',
  'panther',
  'giant-rat',
  'giant-toad',
  'giant-serpent',
  'frost-spider',
  'frost-troll',
  'lava-lizard',
  'nightmare',
  'forest-ostard',
  'desert-ostard',
  'frenzied-ostard',
  'elder-gazer',
  'wisp',
  'swamp-tentacle',
  'orcish-lord',
  'orcish-mage',
  'terathan-matriarch',
  'ophidian-knight-errant',
  'ophidian-matriarch',
  'ice-elemental',
  'poison-elemental',
  'snow-elemental',
  'sea-serpent',
  'deep-sea-serpent',
  'kraken',
  'blade-spirit',
  'energy-vortex',
  'abysmal-horror',
  'darknight-creeper',
  'gibberling',
  'mound-of-maggots',
  'patchwork-skeleton',
  'bone-demon',
  'gore-fiend',
  'devourer',
  'flesh-golem',
  'impaler',
  'wanderer',
  'demon-knight',
  'demon-lord',
  'demon-knight-boss',
  'balron',
  'rikktor',
  'mephitis',
  'semidar',
]);

/** Core Set v3 retains every historical monster and adds four card-only identities. */
export const CORE_SET_V3_MONSTER_IDS: readonly string[] = Object.freeze([
  ...CORE_SET_V1_MONSTER_IDS,
  'pixie',
  'treefellow-guardian',
  'copper-elemental',
  'phoenix',
]);

export interface CardSetDefinition {
  id: CardSetId;
  version: number;
  name: string;
  cardIds: readonly string[];
}

export const CORE_SET_V1: CardSetDefinition = Object.freeze({
  id: 'core-v1',
  version: 1,
  name: 'Core Set v1',
  cardIds: Object.freeze([...CORE_SET_V1_MONSTER_IDS, ...CORE_SET_V1_EQUIPMENT_IDS]),
});

export const CORE_SET_V2: CardSetDefinition = Object.freeze({
  id: 'core-v2',
  version: 2,
  name: 'Core Set v2',
  cardIds: Object.freeze([...CORE_SET_V1_MONSTER_IDS, ...CORE_SET_V2_ACTION_IDS]),
});

export const CORE_SET_V3: CardSetDefinition = Object.freeze({
  id: 'core-v3',
  version: 3,
  name: 'Core Set v3',
  cardIds: Object.freeze([...CORE_SET_V3_MONSTER_IDS, ...CORE_SET_V3_ACTION_IDS]),
});

export const CURRENT_CARD_SET = CORE_SET_V3;

/**
 * The **balance-tuning version** stamped onto every card-match telemetry summary
 * (PRD #638 Slice 4). Bump it on any card tuning/balance change (stat, cost, keyword,
 * effect, or pacing knob) so post-launch metrics remain interpretable across patches -
 * the card-set id above changes only when the released membership changes.
 */
export const CARD_BALANCE_VERSION = 9;

const CURRENT_CARD_ID_SET: ReadonlySet<string> = new Set(CURRENT_CARD_SET.cardIds);
const CURRENT_MONSTER_ID_SET: ReadonlySet<string> = new Set(CORE_SET_V3_MONSTER_IDS);
const CURRENT_ACTION_ID_SET: ReadonlySet<string> = new Set(CORE_SET_V3_ACTION_IDS);

/** Whether an id names a current spell or item card. */
export function isActionCard(id: string): boolean {
  return ACTION_BY_ID.has(id);
}

/** Whether an id names any card in the current released set. */
export function isKnownCard(id: string): boolean {
  return CURRENT_CARD_ID_SET.has(id);
}

/** Build the concrete monster card for one id at one rarity. */
export function monsterCard(id: string, rarity: CardRarity): MonsterCardDef {
  if (!CURRENT_MONSTER_ID_SET.has(id)) throw new Error(`unknown card '${id}'`);
  const def = monsters.get(id);
  const cost = monsterCardCost(id);
  const { attack, health } = monsterCommonStats(id);
  return {
    kind: 'monster',
    setId: CURRENT_CARD_SET.id,
    cardId: id,
    name: def.name,
    rarity,
    cost,
    attack,
    health,
    keywords: CARD_TWEAKS[id]?.keywords ?? [],
    slayerGroup: def.slayerGroup,
    abilities: CARD_TWEAKS[id]?.abilities ?? {},
    body: def.body,
    hue: CARD_HUES_BY_RARITY[id]?.[rarity] ?? def.hue,
  };
}

/** Build the concrete spell or item card for one id at one rarity. */
export function actionCard(id: string, rarity: CardRarity): ActionCardDef {
  if (!CURRENT_ACTION_ID_SET.has(id)) throw new Error(`unknown action card '${id}'`);
  const base = ACTION_BY_ID.get(id);
  if (!base) throw new Error(`unknown action card '${id}'`);
  const effect: ActionEffect = {
    kind: base.effectKind,
    amount: base.amount,
  };
  if (base.buffHealth != null) {
    effect.buffHealth = base.buffHealth;
  }
  if (base.summonDelayTurns != null) effect.summonDelayTurns = base.summonDelayTurns;
  if (base.secondaryAmount != null) effect.secondaryAmount = base.secondaryAmount;
  if (base.maxTargets != null) effect.maxTargets = base.maxTargets;
  if (base.split) effect.split = true;
  if (base.cardIdToSummon) effect.cardId = base.cardIdToSummon;
  if (base.cardIdsToSummon) effect.cardIds = base.cardIdsToSummon;
  const common = {
    setId: CURRENT_CARD_SET.id,
    cardId: id,
    name: base.name,
    rarity,
    cost: base.cost,
    effect,
    category: base.category,
    uoDescription: base.uoDescription,
  };
  if (base.kind === 'spell') {
    return {
      ...common,
      kind: 'spell',
      school: base.school,
      ...(base.circle !== undefined ? { circle: base.circle } : {}),
    };
  }
  return {
    ...common,
    kind: 'item',
    itemType: base.itemType,
  };
}

/** The concrete card for any current-set id at any rarity (throws on an unknown id). */
export function cardDef(id: string, rarity: CardRarity): CardDef {
  if (CURRENT_MONSTER_ID_SET.has(id)) return monsterCard(id, rarity);
  if (CURRENT_ACTION_ID_SET.has(id)) return actionCard(id, rarity);
  throw new Error(`unknown card '${id}'`);
}

// ---------------------------------------------------------------------------
// The Coin - the second player's holdable compensation token (PRD #638 Slice 2).
// ---------------------------------------------------------------------------

/**
 * The Coin's card id. Deliberately **not collectible**: it is absent from every
 * released set's membership, so {@link isKnownCard} is false, {@link cardDef}
 * throws, {@link validateDeck} rejects any deck naming it, and the pack pool
 * ({@link ../sim/cardPacks.rollPack} draws from {@link allCardIds}) can never
 * emit it. The battle engine deals exactly one into the second player's opening
 * hand ({@link ../sim/cardGame.startCardMatch}) and it exists nowhere else - no
 * authored effect summons or draws it.
 */
export const COIN_CARD_ID = 'the-coin';

/** The Coin's display name. */
export const COIN_CARD_NAME = 'The Coin';

/**
 * The Coin's concrete definition: a zero-cost token item whose one effect grants
 * one **temporary** mana for the current turn (spendable this turn only - it can
 * exceed the crystal count by one, never raises `maxMana`, and any unspent part
 * expires when the turn ends). Built by hand rather than through {@link cardDef}
 * because the Coin is match-transient, not set content; the engine and the
 * client card face both read this one definition. Rarity never changes a card
 * identity's gameplay values, and the Coin is never collected; it prints as a common.
 */
export function coinCardDef(rarity: CardRarity = 'common'): ItemCardDef {
  return {
    kind: 'item',
    setId: CURRENT_CARD_SET.id,
    cardId: COIN_CARD_ID,
    name: COIN_CARD_NAME,
    rarity,
    cost: 0,
    effect: { kind: 'gain-mana', amount: 1 },
    category: 'utility',
    itemType: 'token',
    uoDescription: 'A single Britannian gold coin, flipped to decide who moves first.',
  };
}

/** Every distinct current card name, each of which exists in four rarities. */
export function allCardIds(): string[] {
  return [...CURRENT_CARD_SET.cardIds];
}

/** Every concrete card definition - each name x each rarity. */
export function allCardDefs(): CardDef[] {
  return allCardIds().flatMap((id) => CARD_RARITIES.map((r) => cardDef(id, r)));
}

// ---------------------------------------------------------------------------
// Decks.
// ---------------------------------------------------------------------------

/** One deck slot: a card name at a chosen rarity. */
export interface DeckCardRef {
  cardId: string;
  rarity: CardRarity;
}

/** A deck is an ordered list of card refs (legal only at exactly {@link DECK_SIZE}). */
export type DeckList = readonly DeckCardRef[];

/**
 * One durable **collection** entry (PRD #493 Slice 2 persistence): how many copies
 * of one concrete card - a `cardId` at a `rarity` - the account owns. The unit is
 * the `(cardId, rarity)` pair, so a common Dragon and a legendary Dragon are two
 * distinct entries; `count` accumulates duplicates (kept beyond the deck limit as
 * future trade stock, PRD story 28). Plain data the account store persists; the
 * store's `addCard` merges by pair.
 */
export interface CardCollectionEntry {
  cardId: string;
  rarity: CardRarity;
  /** Copies owned (>= 1 for a persisted entry). */
  count: number;
}

/**
 * One saved, named deck (PRD #493 Slice 2 / story 10): a player-chosen `name` over
 * an ordered {@link DeckList}. An account keeps up to {@link MAX_NAMED_DECKS} of
 * these. The stored card list is *not* required to be a legal 20-card deck at rest
 * (the deck-builder, Slice 3, validates on save via {@link validateDeck}); the
 * store persists the ordered refs as given.
 */
export interface NamedDeck {
  name: string;
  cards: DeckCardRef[];
  /** Explicit preferred deck. When absent on every deck, the first deck is the default. */
  default?: boolean;
}

/** Resolve the preferred deck, retaining index-zero compatibility for older accounts. */
export function defaultNamedDeckIndex(decks: readonly NamedDeck[]): number | undefined {
  if (decks.length === 0) return undefined;
  const explicit = decks.findIndex((deck) => deck.default === true);
  return explicit >= 0 ? explicit : 0;
}

/** The most named decks one account may keep (PRD story 10: "up to 3"). */
export const MAX_NAMED_DECKS = 3;

/**
 * The starter grant's collection, as `(cardId, rarity, count)` entries: the
 * {@link STARTER_DECK} refs collapsed by pair. All commons, so this is exactly the
 * 20 cards the new player owns after the one-time grant (PRD story 13). Pure and
 * order-stable (first-seen pair order) so the grant is reproducible.
 */
export function starterCollectionEntries(): CardCollectionEntry[] {
  const entries: CardCollectionEntry[] = [];
  const index = new Map<string, CardCollectionEntry>();
  for (const ref of STARTER_DECK) {
    const key = `${ref.cardId}:${ref.rarity}`;
    const existing = index.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      const entry: CardCollectionEntry = { cardId: ref.cardId, rarity: ref.rarity, count: 1 };
      index.set(key, entry);
      entries.push(entry);
    }
  }
  return entries;
}

/** The display name the granted starter deck is saved under. */
export const STARTER_DECK_NAME = 'Starter';

/**
 * The starter grant's ready-to-play saved deck (PRD story 13, "battle within
 * minutes"): the {@link STARTER_DECK} refs under {@link STARTER_DECK_NAME}, so a
 * freshly-granted account has both the cards *and* a legal deck to challenge with.
 * A fresh copy of the refs each call (never aliases the module constant).
 */
export function starterNamedDeck(): NamedDeck {
  return {
    name: STARTER_DECK_NAME,
    cards: STARTER_DECK.map((ref) => ({ cardId: ref.cardId, rarity: ref.rarity })),
  };
}

/** The result of a deck-legality check - `ok` with no error, or the first violation. */
export interface DeckValidation {
  ok: boolean;
  error?: string;
}

/**
 * Deck legality: exactly {@link DECK_SIZE} cards, at most
 * {@link MAX_COPIES_PER_NAME} of any card **name** regardless of rarity, and
 * every ref a known card at a valid rarity. A common Dragon plus a legendary
 * Dragon is a legal pair (two copies of the name "Dragon"); a third is not.
 */
export function validateDeck(deck: DeckList): DeckValidation {
  if (deck.length !== DECK_SIZE) {
    return { ok: false, error: `deck must be exactly ${DECK_SIZE} cards (has ${deck.length})` };
  }
  const perName = new Map<string, number>();
  for (const ref of deck) {
    if (!isKnownCard(ref.cardId)) return { ok: false, error: `unknown card '${ref.cardId}'` };
    if (!CARD_RARITIES.includes(ref.rarity)) return { ok: false, error: `invalid rarity '${ref.rarity}'` };
    const n = (perName.get(ref.cardId) ?? 0) + 1;
    perName.set(ref.cardId, n);
    if (n > MAX_COPIES_PER_NAME) {
      return { ok: false, error: `too many copies of '${ref.cardId}' (max ${MAX_COPIES_PER_NAME})` };
    }
  }
  return { ok: true };
}

/** How many copies of a card **name** (any rarity) a deck-in-progress already holds. */
export function deckNameCount(deck: DeckList, cardId: string): number {
  let n = 0;
  for (const ref of deck) if (ref.cardId === cardId) n += 1;
  return n;
}

/**
 * Whether one more copy of `cardId` could be added to a deck-in-progress without
 * breaking legality (the deck-builder's per-add guard, PRD story 12). It reuses
 * the same {@link DECK_SIZE} / {@link MAX_COPIES_PER_NAME} limits {@link
 * validateDeck} enforces, so the editor can never diverge from the legality rules:
 * the deck is not already full, and this name is not already at its copy cap. Does
 * **not** check ownership - that is the collection's job ({@link
 * deckWithinCollection}); this is purely the size/copies rule.
 */
export function canAddToDeck(deck: DeckList, cardId: string): boolean {
  return deck.length < DECK_SIZE && deckNameCount(deck, cardId) < MAX_COPIES_PER_NAME;
}

/** Copies of one concrete card - a `(cardId, rarity)` pair - the collection owns (0 if none). */
export function collectionCount(
  collection: readonly CardCollectionEntry[],
  cardId: string,
  rarity: CardRarity,
): number {
  for (const e of collection) if (e.cardId === cardId && e.rarity === rarity) return e.count;
  return 0;
}

/**
 * Whether a deck uses only cards the collection actually owns - no `(cardId,
 * rarity)` pair appears in the deck more times than the account holds copies of it.
 * The ownership half of deck legality (a hacked client cannot deck cards it never
 * pulled); the server validates a saved deck against the authoritative collection
 * with this, the way `handleEquip` validates worn cosmetics against the owned set.
 */
export function deckWithinCollection(
  deck: DeckList,
  collection: readonly CardCollectionEntry[],
): boolean {
  const used = new Map<string, number>();
  for (const ref of deck) {
    const key = `${ref.cardId}:${ref.rarity}`;
    const n = (used.get(key) ?? 0) + 1;
    used.set(key, n);
    if (n > collectionCount(collection, ref.cardId, ref.rarity)) return false;
  }
  return true;
}

/** Convenience: how many copies each card name would need `n` of. */
function copies(cardId: string, n: number, rarity: CardRarity = 'common'): DeckCardRef[] {
  return Array.from({ length: n }, () => ({ cardId, rarity }));
}

/**
 * The free starter deck new reserved names receive (PRD story 13): exactly 20
 * commons on a gentle mana curve, and the fairness baseline the balance harness
 * mirrors. All commons, at most two of any name.
 */
export const STARTER_DECK: DeckList = [
  // The list order below is load-bearing for the balance harness's seeded
  // shuffles - append/replace in place rather than re-sorting by cost.
  // 1-drops (3): a fast striker and two Slime bodies (plain since the
  // ADR-0340 texture wave stripped their off-identity Taunt).
  ...copies('mongbat', 1),
  ...copies('slime', 2),
  // 2- and 3-drops: Charge pressure, a spider body, a Reaper (3 since the
  // ADR-0331 curve tuning).
  ...copies('dire-wolf', 2),
  ...copies('hell-hound', 1),
  ...copies('giant-spider', 1),
  ...copies('reaper', 1),
  // 3-drops: sticky Undead bodies.
  ...copies('zombie', 2),
  ...copies('skeleton', 2),
  ...copies('spirit', 1),
  // 4- and 5-drops: vanilla bodies, an Ettin (Taunt), a Lizardman (5 since
  // ADR-0331).
  ...copies('orc', 2),
  ...copies('lizardman', 1),
  ...copies('ettin', 1),
  // A 5-drop Ogre (Taunt) wall.
  ...copies('ogre', 1),
  // Spells for reach and sustain (2): Fireball (3/15) and the
  // Heal Potion.
  ...copies('fireball', 1),
  ...copies('healing-potion', 1),
];

/** Build a common-rarity reference deck with two copies of each supplied name. */
function pairedDeck(cardIds: readonly string[]): DeckList {
  return cardIds.flatMap((cardId) => copies(cardId, 2));
}

/**
 * Arachnid deck: early Slayer Group pressure backed by a balanced neutral curve.
 * The ADR-0340 texture wave swapped Scorpion for the same-cost Slime: the
 * Scorpion's new reactive Poison chip depressed the mirror's starting-seat rate
 * below the gate, and the post-wave vanilla Slime carries the exact 3/6
 * statline the pre-wave Scorpion anchored this recipe with. PRD #639's
 * reauthored recipes draw venom from the pool's deepened Poison texture.
 */
export const ARACHNID_DECK: DeckList = pairedDeck([
  'giant-spider', 'slime', 'dread-spider', 'dire-wolf', 'forest-ostard',
  'panther', 'orc', 'ettin', 'ogre', 'explosion',
]);

/**
 * Grinding Undead deck: sticky deaths, healing, armor, and graveyard recovery.
 * Spirit replaced Rotting Corpse to keep the post-migration reference matrix
 * balanced; the ADR-0340 texture wave then swapped Spirit for the same-cost
 * Skeleton Warrior - Spirit's new Drain pushed the Undead-vs-Demon pairing
 * past the band, and the Protection wall restores the mirror's seat balance
 * the stripped Slime Taunt had been providing.
 */
export const UNDEAD_DECK: DeckList = pairedDeck([
  'mongbat', 'slime', 'dire-wolf', 'hell-hound', 'zombie',
  'skeleton', 'skeleton-warrior', 'orc', 'ogre', 'animate-dead',
]);

/**
 * Demon deck: early cultists and summons bridge into signature finishers.
 * Its post-migration curve preserves the reference matrix after unsupported Demon
 * overrides were removed from Mound of Maggots and Gore Fiend.
 */
export const DEMON_DECK: DeckList = pairedDeck([
  'gibberling', 'slime', 'mound-of-maggots', 'hell-hound', 'dire-wolf',
  'gore-fiend', 'orc', 'gargoyle', 'flame-strike', 'summon-daemon',
]);

/** Stable ids accepted by the one-time starter grant and practice-loaner paths. */
export const STARTER_RECIPE_IDS = ['britannian', 'arachnid', 'undead', 'demon'] as const;
export type StarterRecipeId = typeof STARTER_RECIPE_IDS[number];

export interface StarterRecipe {
  id: StarterRecipeId;
  name: string;
  archetype: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  traits: readonly string[];
  summary: string;
  cards: DeckList;
}

/** The four authored recipes. Visibility is deliberately independent of ownership. */
export const STARTER_RECIPES: readonly StarterRecipe[] = Object.freeze([
  {
    id: 'britannian',
    name: 'Britannian Basics',
    archetype: 'Balanced',
    difficulty: 'Beginner',
    traits: ['Sturdy', 'Healing', 'Direct damage'],
    summary: 'A straightforward curve of sturdy minions, direct damage, healing, Taunt, and Charge.',
    cards: STARTER_DECK,
  },
  {
    id: 'arachnid',
    name: 'Arachnid Swarm',
    archetype: 'Swarm',
    difficulty: 'Intermediate',
    traits: ['Wide boards', 'Poison', 'Group payoff'],
    summary: 'Build a wide board, threaten poisonous trades, and finish with a group payoff.',
    cards: ARACHNID_DECK,
  },
  {
    id: 'undead',
    name: 'Undead Requiem',
    archetype: 'Graveyard',
    difficulty: 'Intermediate',
    traits: ['Resilient', 'Recovery', 'Attrition'],
    summary: 'Turn fallen minions and the graveyard into finite, repeatable value.',
    cards: UNDEAD_DECK,
  },
  {
    id: 'demon',
    name: 'Demon Host',
    archetype: 'Late game',
    difficulty: 'Advanced',
    traits: ['Summons', 'Big threats', 'Finishers'],
    summary: 'Use cheap servants and summons to survive until powerful late-game Demons arrive.',
    cards: DEMON_DECK,
  },
]);

export function isStarterRecipeId(value: unknown): value is StarterRecipeId {
  return typeof value === 'string' &&
    (STARTER_RECIPE_IDS as readonly string[]).includes(value);
}

export function starterRecipe(id: StarterRecipeId): StarterRecipe {
  return STARTER_RECIPES.find((recipe) => recipe.id === id)!;
}

export function recipeCollectionEntries(id: StarterRecipeId): CardCollectionEntry[] {
  const entries: CardCollectionEntry[] = [];
  for (const ref of starterRecipe(id).cards) {
    const existing = entries.find(
      (entry) => entry.cardId === ref.cardId && entry.rarity === ref.rarity,
    );
    if (existing) existing.count += 1;
    else entries.push({ ...ref, count: 1 });
  }
  return entries;
}

export function recipeNamedDeck(id: StarterRecipeId): NamedDeck {
  const recipe = starterRecipe(id);
  return {
    name: recipe.name,
    cards: recipe.cards.map((ref) => ({ ...ref })),
  };
}

/** Seeded archetype pool Card Master Carl selects from in free practice games. */
export const CARD_MASTER_DECKS: readonly DeckList[] = STARTER_RECIPES.map((recipe) => recipe.cards);
export type CardArchetype = StarterRecipeId;

/** Select Carl's practice archetype from the caller's seeded RNG. */
export function chooseCardMasterDeck(rng: () => number): DeckList {
  return CARD_MASTER_DECKS[Math.floor(rng() * CARD_MASTER_DECKS.length)]!;
}

/**
 * Build Carl's seeded four-deck rotation.
 * The RNG chooses the cycle's order once, then every four consecutive practice
 * matches cover all archetypes without repeats or process-global state.
 */
export function createCardMasterDeckRotation(rng: () => number): () => DeckList {
  const cycle = [...CARD_MASTER_DECKS];
  for (let i = cycle.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [cycle[i], cycle[j]] = [cycle[j]!, cycle[i]!];
  }
  let cursor = 0;
  return () => cycle[cursor++ % cycle.length]!;
}

/** Stable diagnostic name for one of Carl's reference decks. */
export function cardMasterDeckArchetype(deck: DeckList): CardArchetype {
  if (deck === STARTER_DECK) return 'britannian';
  if (deck === ARACHNID_DECK) return 'arachnid';
  if (deck === UNDEAD_DECK) return 'undead';
  return 'demon';
}
